import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';
import { useTheme } from '../../contexts/ThemeContext';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { isDarkMode } = useTheme();

  const handleResetPassword = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService?.resetPassword(email);
      setSuccess(true);
    } catch (err) {
      setError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-4 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' :'bg-gradient-to-br from-blue-50 via-violet-50 to-pink-50'
      }`}>
        <div className="max-w-md w-full">
          <div className="bg-card rounded-xl shadow-warm-xl p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="mail" size={32} className="text-blue-600" />
            </div>
            <h2 className="text-h3 font-heading text-foreground mb-2">
              Check Your Email
            </h2>
            <p className="text-muted-foreground mb-6">
              We have sent a password reset link to <strong>{email}</strong>
            </p>
            <Link to="/signin">
              <Button fullWidth variant="outline">
                Back to Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Reset Password - Starling Schedule</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-card rounded-xl shadow-warm-xl p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <AppImage
                src="/assets/images/ad4cbaf4-dc2b-4059-a1d5-7af12fa38f76_copy-1767800870676.jpg"
                alt="Starling Family Planner Logo"
                className="w-24 h-24 object-contain"
              />
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-h2 font-heading text-foreground mb-2">
                Reset Password
              </h1>
              <p className="text-muted-foreground">
                Enter your email to receive a reset link
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-start gap-3">
                <Icon name="alert-circle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Reset Form */}
            <form onSubmit={handleResetPassword} className="space-y-4 mb-6">
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                placeholder="your@email.com"
                required
              />

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                iconName={loading ? 'loader' : 'mail'}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>

            {/* Link to Sign In */}
            <div className="text-center text-sm text-muted-foreground">
              Remember your password?{' '}
              <Link to="/signin" className="text-primary hover:underline font-medium">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;